import { type GridRowId } from "@mui/x-data-grid-pro";
import type {
  BulkAcquisitionItem,
  BulkPropertyRow,
  BulkReusePicture,
  ReuseCandidate,
} from "../types";
import type { AcquirePolicy } from "./acquirePolicy";
import { buildBulkAcquisitionItems } from "./bulkPictureRequestPayload";
import { isUsableByWindow, othersReusablePictures } from "./reuseAvailability";
import { findCandidates } from "./reuseCandidates";
import { sameFields } from "./zenbujikoConstruction";

export interface ReuseSelectionResult {
  items: BulkAcquisitionItem[];
  /** 新規取得になる書類の件数（課金対象） */
  newPictureCount: number;
  /** 自社の取得済み登記を使う書類の件数 */
  acquiredPictureCount: number;
  /** 他社のリユース登記を使う書類の件数 */
  reusePictureCount: number;
  /** 候補がなく依頼から除外した書類の件数（新規取得を使わない条件でのみ発生） */
  excludedPictureCount: number;
  /** 全書類が除外され依頼から丸ごと外れた物件の件数 */
  excludedPropertyCount: number;
}

export type PictureSelection =
  | { kind: "acquired" | "reuse"; candidate: ReuseCandidate }
  | { kind: "new" }
  | { kind: "excluded" };

/**
 * 1書類を policy.steps の順に振り分ける。取得済み・リユースは時間窓内の候補だけを使い
 * （ignoreWindow なら取得日を問わない）、新規取得は常に成立する。末尾まで候補がなければ除外。
 * ZENBUJIKO は zenbujikoOptions と集合一致する候補だけを使う（構成違いの候補が複数並ぶため）。
 * 送信・フッター内訳・全部事項の取得方法セルが同じ判定を共有する
 */
export const selectPictureSource = (
  row: BulkPropertyRow | undefined,
  pictureType: string,
  zenbujikoOptions: readonly string[],
  policy: AcquirePolicy,
  pastDays: number,
  now: Date
): PictureSelection => {
  // 使える候補のうち取得日が最新のもの（候補は最新順）
  const pickUsable = (
    candidates: ReuseCandidate[]
  ): ReuseCandidate | undefined =>
    findCandidates(candidates, pictureType).find(
      (candidate) =>
        isUsableByWindow(candidate, policy, pastDays, now) &&
        (pictureType !== "ZENBUJIKO" ||
          sameFields(candidate.zenbujikoOptions, zenbujikoOptions))
    );

  for (const step of policy.steps) {
    if (step === "new") {
      return { kind: "new" };
    }
    const candidate = pickUsable(
      step === "acquired"
        ? row?.acquiredPictures ?? []
        : row == null
        ? []
        : othersReusablePictures(row)
    );
    if (candidate != null) {
      return { kind: step, candidate };
    }
  }
  return { kind: "excluded" };
};

/**
 * buildBulkAcquisitionItems の出力（genzai 変換済み）に取得条件×経過日数の選別を適用し、
 * 書類ごとに「リユース（reusePictures に ID を積む）/ 新規 / 除外」へ振り分ける。
 * ZENBUJIKO をリユースする場合、依頼側の zenbujikoOptions は候補の構成へ置き換える
 */
export const buildReuseAwareItems = (
  baseItems: BulkAcquisitionItem[],
  rowsByPropertyCode: Map<string, BulkPropertyRow>,
  policy: AcquirePolicy,
  pastDays: number,
  now: Date
): ReuseSelectionResult => {
  const items: BulkAcquisitionItem[] = [];
  let newPictureCount = 0;
  let acquiredPictureCount = 0;
  let reusePictureCount = 0;
  let excludedPictureCount = 0;
  let excludedPropertyCount = 0;

  for (const baseItem of baseItems) {
    const row = rowsByPropertyCode.get(baseItem.propertyCode);
    const selectCandidate = (pictureType: string): PictureSelection =>
      selectPictureSource(
        row,
        pictureType,
        baseItem.zenbujikoOptions,
        policy,
        pastDays,
        now
      );

    const pictures: string[] = [];
    const reusePictures: BulkReusePicture[] = [];
    let zenbujikoOptions = baseItem.zenbujikoOptions;

    for (const pictureType of baseItem.pictures) {
      const selected = selectCandidate(pictureType);
      if (selected.kind === "excluded") {
        excludedPictureCount += 1;
        if (pictureType === "ZENBUJIKO") {
          zenbujikoOptions = [];
        }
        continue;
      }
      pictures.push(pictureType);
      if (selected.kind === "new") {
        newPictureCount += 1;
        continue;
      }
      if (selected.kind === "acquired") {
        acquiredPictureCount += 1;
      } else {
        reusePictureCount += 1;
      }
      reusePictures.push({
        picture: pictureType,
        reuseDetailId: selected.candidate.detailId,
      });
      if (pictureType === "ZENBUJIKO") {
        // BE の集合一致検証に合わせて候補の構成をそのまま往復する
        zenbujikoOptions = selected.candidate.zenbujikoOptions;
      }
    }

    if (pictures.length === 0) {
      excludedPropertyCount += 1;
      continue;
    }
    items.push({ ...baseItem, pictures, zenbujikoOptions, reusePictures });
  }

  return {
    items,
    newPictureCount,
    acquiredPictureCount,
    reusePictureCount,
    excludedPictureCount,
    excludedPropertyCount,
  };
};

/**
 * チェック状態から確認ダイアログ・グリッドフッターの内訳を組み立てる。
 * buildBulkAcquisitionItems と buildReuseAwareItems の呼び出しを1箇所にまとめ、
 * 両呼び出し元での重複を防ぐ
 */
export const selectCheckedItems = (
  rows: BulkPropertyRow[],
  checkedIds: Record<string, Set<GridRowId>>,
  policy: AcquirePolicy,
  pastDays: number,
  now: Date
): ReuseSelectionResult & {
  rowsByPropertyCode: Map<string, BulkPropertyRow>;
} => {
  const rowsByPropertyCode = new Map(
    rows.map((row) => [row.propertyCode, row])
  );
  return {
    ...buildReuseAwareItems(
      buildBulkAcquisitionItems(rows, checkedIds),
      rowsByPropertyCode,
      policy,
      pastDays,
      now
    ),
    rowsByPropertyCode,
  };
};
